import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useState,
  useCallback,
  JSX,
} from 'react';
import { IDeliveryInstructionInfo } from '../models/delivery-instructions';
import {
  catchEditDeliveryInstructionsButtonPress,
  catchProSiteInstructionsOverlayOpened,
} from '../utils/edit-delivery-instructions/analytics';

export interface IEditDeliveryInstructionsContext {
  deliveryInstructions: string;
  setDeliveryInstructions: Dispatch<SetStateAction<string>>;
  editDeliveryInstructionsDrawerOpen: boolean;
  setEditDeliveryInstructionsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  deliveryInstructionInfo: IDeliveryInstructionInfo;
  setDeliveryInstructionInfo: Dispatch<
    SetStateAction<IDeliveryInstructionInfo>
  >;
  handleEditDeliveryInstructions: (
    deliveryInstructionInfo: IDeliveryInstructionInfo,
  ) => void;
  deliveryType: string;
  setDeliveryType: Dispatch<SetStateAction<string>>;
  propertyTypesList: string[];
  fulfillmentType: string;
  securityCode: string;
  setSecurityCode: Dispatch<SetStateAction<string>>;
  lockboxLocation: string;
  setLockboxLocation: Dispatch<SetStateAction<string>>;
  lockboxCode: string;
  setLockboxCode: Dispatch<SetStateAction<string>>;
  preferredPackagePlacement: string;
  setPreferredPackagePlacement: Dispatch<SetStateAction<string>>;
  propertyType: string;
  setPropertyType: Dispatch<SetStateAction<string>>;
  siteConditions: string;
  setSiteConditions: Dispatch<SetStateAction<string>>;
  additionalInstructions: string;
  setAdditionalInstructions: Dispatch<SetStateAction<string>>;
  otherPropertyType: string;
  setOtherPropertyType: Dispatch<SetStateAction<string>>;
  hasStairs: boolean | null;
  setHasStairs: Dispatch<SetStateAction<boolean | null>>;
  trucks36Foot: boolean;
  setTrucks36Foot: Dispatch<SetStateAction<boolean>>;
}

interface IEditDeliveryInstructionsProvider {
  children: JSX.Element | JSX.Element[];
}

export const EditDeliveryInstructionsContext =
  createContext<IEditDeliveryInstructionsContext>({
    deliveryInstructions: '',
    setDeliveryInstructions: () => '',
    editDeliveryInstructionsDrawerOpen: false,
    setEditDeliveryInstructionsDrawerOpen: () => null,
    deliveryInstructionInfo: {} as IDeliveryInstructionInfo,
    setDeliveryInstructionInfo: () => null,
    handleEditDeliveryInstructions: () => null,
    deliveryType: '',
    setDeliveryType: () => '',
    propertyTypesList: [],
    fulfillmentType: '',
    securityCode: '',
    setSecurityCode: () => '',
    lockboxLocation: '',
    setLockboxLocation: () => '',
    lockboxCode: '',
    setLockboxCode: () => '',
    preferredPackagePlacement: '',
    setPreferredPackagePlacement: () => '',
    propertyType: '',
    setPropertyType: () => '',
    siteConditions: '',
    setSiteConditions: () => '',
    additionalInstructions: '',
    setAdditionalInstructions: () => '',
    otherPropertyType: '',
    setOtherPropertyType: () => '',
    hasStairs: null,
    setHasStairs: () => null,
    trucks36Foot: false,
    setTrucks36Foot: () => false,
  });

export const EditDeliveryInstructionsProvider = ({
  children,
}: IEditDeliveryInstructionsProvider) => {
  const [deliveryInstructions, setDeliveryInstructions] = useState<string>('');
  const [
    editDeliveryInstructionsDrawerOpen,
    setEditDeliveryInstructionsDrawerOpen,
  ] = useState<boolean>(false);
  const [deliveryInstructionInfo, setDeliveryInstructionInfo] =
    useState<IDeliveryInstructionInfo>({} as IDeliveryInstructionInfo);

  const [deliveryType, setDeliveryType] = useState<string>('');
  const propertyTypesList = [
    'House',
    'Apartment',
    'Business',
    'Construction Site',
    'Other',
  ];

  const [securityCode, setSecurityCode] = useState<string>('');
  const [lockboxLocation, setLockboxLocation] = useState<string>('');
  const [lockboxCode, setLockboxCode] = useState<string>('');
  const [preferredPackagePlacement, setPreferredPackagePlacement] =
    useState<string>('');
  const [propertyType, setPropertyType] = useState<string>('');
  const [siteConditions, setSiteConditions] = useState<string>('');
  const [additionalInstructions, setAdditionalInstructions] =
    useState<string>('');
  const [otherPropertyType, setOtherPropertyType] = useState<string>('');
  const [hasStairs, setHasStairs] = useState<boolean | null>(null);
  const [trucks36Foot, setTrucks36Foot] = useState<boolean>(false);

  const handleEditDeliveryInstructions = useCallback(
    (instructionInfo: IDeliveryInstructionInfo) => {
      setDeliveryInstructionInfo(instructionInfo);
      setEditDeliveryInstructionsDrawerOpen(true);

      // Track overlay open event based on delivery instruction type
      if (instructionInfo.isEditSiteInstructionsEnabled) {
        // Pro Site Instructions (DFS)
        catchProSiteInstructionsOverlayOpened();
      } else {
        // Standard Delivery Instructions
        catchEditDeliveryInstructionsButtonPress();
      }
    },
    [],
  );

  return (
    <EditDeliveryInstructionsContext.Provider
      value={{
        deliveryInstructions,
        setDeliveryInstructions,
        editDeliveryInstructionsDrawerOpen,
        setEditDeliveryInstructionsDrawerOpen,
        deliveryInstructionInfo,
        setDeliveryInstructionInfo,
        handleEditDeliveryInstructions,
        deliveryType,
        setDeliveryType,
        propertyTypesList,
        fulfillmentType: deliveryInstructionInfo.fulfillmentType || '', // Changes every time deliveryInstructionInfo changes
        securityCode,
        setSecurityCode,
        lockboxLocation,
        setLockboxLocation,
        lockboxCode,
        setLockboxCode,
        preferredPackagePlacement,
        setPreferredPackagePlacement,
        propertyType,
        setPropertyType,
        siteConditions,
        setSiteConditions,
        additionalInstructions,
        setAdditionalInstructions,
        otherPropertyType,
        setOtherPropertyType,
        hasStairs,
        setHasStairs,
        trucks36Foot,
        setTrucks36Foot,
      }}
    >
      {children}
    </EditDeliveryInstructionsContext.Provider>
  );
};
